import { useEffect } from 'react';
import { create } from 'zustand';
import type { JackpotResponse, JackpotWonEvent } from '@tg/shared';
import { endpoints } from '@/api/endpoints';

/**
 * «乐游 Jackpot» (docs/superpowers/specs/2026-10-01-jackpot-design.md): the pool, the ceiling and the last winners.
 * Loaded once (GET /api/jackpot) and kept live by the socket (`jackpot:update`, `jackpot:won` — JackpotListener).
 * `enabled: false` (switched off, or `reason: 'region'` — a country of JACKPOT_BLOCKED_COUNTRIES): every widget is
 * hidden and the socket events are ignored (the server does not send them to such sockets anyway).
 */
interface JackpotStore {
  data: JackpotResponse | null;
  loaded: boolean;
  loading: boolean;
  /** the player's own win waiting for (or showing) its celebration */
  mine: JackpotWonEvent | null;
  load(): Promise<void>;
  setPool(pool: number): void;
  won(e: JackpotWonEvent, own: boolean): void;
  dismiss(): void;
}

let refetch: ReturnType<typeof setTimeout> | null = null;

export const useJackpotStore = create<JackpotStore>()((set, get) => ({
  data: null,
  loaded: false,
  loading: false,
  mine: null,
  async load() {
    if (get().loading) return;
    set({ loading: true });
    let data: JackpotResponse | null = null;
    try {
      data = await endpoints.jackpot();
    } catch {
      // offline / old server: the jackpot stays hidden (never blocks a page)
      data = null;
    }
    set({ data, loaded: true, loading: false });
  },
  setPool(pool) {
    const d = get().data;
    // off for this player (JACKPOT_ENABLED=0, or a country without the jackpot — reason 'region'): events are ignored
    if (d?.enabled) set({ data: { ...d, pool } });
  },
  won(e, own) {
    const d = get().data;
    if (d && !d.enabled) return;
    if (own) set({ mine: e });
    // the winners list carries proofs: reload it shortly (the new pool comes with it)
    if (refetch) clearTimeout(refetch);
    refetch = setTimeout(() => {
      refetch = null;
      void get().load();
    }, 600);
  },
  dismiss() {
    set({ mine: null });
  },
}));

/** The jackpot when it is on, else null; loads once on first use. */
export function useJackpot(): JackpotResponse | null {
  const data = useJackpotStore((s) => s.data);
  const loaded = useJackpotStore((s) => s.loaded);
  useEffect(() => {
    if (!loaded) void useJackpotStore.getState().load();
  }, [loaded]);
  return data?.enabled ? data : null;
}
